// Liste sayfası KPI özet kartları — ListPageHeader'ın altına gelir.
//
// Kullanım:
//   <ListPageStats stats={[
//     { label: 'Toplam Ürün',   value: 1384, delta: +47, deltaTooltip: 'Geçen aya göre' },
//     { label: 'Kritik Seviye', value: 12,   color: 'warning' },
//     { label: 'Tükenmiş',      value: 8,    color: 'danger' },
//     { label: 'Hareketsiz',    value: 234,  color: 'neutral' },
//   ]} loading={false} compact={false} />
//
// Her stat şunları içerebilir:
//   - label   (zorunlu)
//   - value   (zorunlu — sayı veya string)
//   - delta   (opsiyonel — pozitif yeşil, negatif kırmızı rozet)
//   - deltaTooltip (opsiyonel — rozet üzerinde hover tooltip)
//   - color   ('primary' | 'success' | 'warning' | 'danger' | 'neutral' — default primary)
//   - icon    (opsiyonel JSX)
//   - onClick (opsiyonel — kart tıklanır olur)
//   - sublabel (opsiyonel — alt satırda küçük not, örn "₺ olarak")

import React, { useState, useEffect } from 'react';

const COLOR_MAP = {
  primary: { fg: 'var(--accent-primary)', bg: 'var(--accent-glow)' },
  success: { fg: 'var(--success)',        bg: 'rgba(22,163,74,0.10)' },
  warning: { fg: 'var(--warning)',        bg: 'rgba(245,158,11,0.10)' },
  danger:  { fg: 'var(--danger)',         bg: 'rgba(220,38,38,0.10)' },
  neutral: { fg: 'var(--text-secondary)', bg: 'var(--bg-secondary)' },
};
const DEFAULT_COLOR = COLOR_MAP.neutral;

function fmtNumber(v) {
  if (v == null) return '—';
  if (typeof v === 'number') {
    // 1234.56 -> "1.234,56" ; 1234 -> "1.234"
    const isInt = Number.isInteger(v);
    return v.toLocaleString('tr-TR', {
      minimumFractionDigits: isInt ? 0 : 2,
      maximumFractionDigits: 2,
    });
  }
  return String(v);
}

function DeltaBadge({ delta, tooltip }) {
  if (delta == null || delta === 0) return null;
  const positive = delta > 0;
  return (
    <span
      title={tooltip || (positive ? 'Artış' : 'Azalış')}
      style={{
        marginLeft: 8,
        padding: '2px 7px',
        borderRadius: 4,
        fontSize: '0.72rem',
        fontWeight: 700,
        fontFamily: 'monospace',
        background: positive ? 'rgba(22,163,74,0.14)' : 'rgba(220,38,38,0.14)',
        color: positive ? 'var(--success)' : 'var(--danger)',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
      }}
    >
      {positive ? '+' : ''}{typeof delta === 'number' ? fmtNumber(delta) : delta}
    </span>
  );
}

export default function ListPageStats({
  stats = [], loading = false, compact = false,
  collapsible = true,            // varsayılan: gizlenebilir (toggle ile)
  defaultOpen = false,           // varsayılan: kapalı (mobilde yer kazandırır)
  storageKey = null,             // verilirse açık/kapalı tercihi localStorage'a yazılır
}) {
  const lsKey = storageKey ? `list-page-stats-open:${storageKey}` : null;
  const [open, setOpen] = useState(() => {
    if (!collapsible) return true;
    if (lsKey) {
      const v = localStorage.getItem(lsKey);
      if (v === '1') return true;
      if (v === '0') return false;
    }
    return defaultOpen;
  });
  useEffect(() => {
    if (lsKey) localStorage.setItem(lsKey, open ? '1' : '0');
  }, [open, lsKey]);

  if (!stats || stats.length === 0) return null;

  // Özet rozet (kapalı durumda gösterilen mini-bar): her stat için renkli sayı
  if (collapsible && !open) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 10px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-secondary)',
            borderRadius: 'var(--r-full)',
            cursor: 'pointer',
            fontSize: '0.78rem',
            color: 'var(--text-secondary)',
          }}
          title="Özet kartlarını göster"
        >
          📊 Analiz <span style={{ color: 'var(--text-tertiary)' }}>▼</span>
        </button>
        {stats.map((s, idx) => {
          const c = COLOR_MAP[s.color || 'primary'] || DEFAULT_COLOR;
          return (
            <span key={s.key || s.label || idx}
              style={{
                fontSize: '0.72rem', padding: '3px 8px', borderRadius: 'var(--r-full)',
                background: c.bg, color: c.fg, fontWeight: 700,
                whiteSpace: 'nowrap',
              }}>
              {loading ? '…' : fmtNumber(s.value)} <span style={{ opacity: 0.7, fontWeight: 500 }}>{s.label}</span>
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      {collapsible && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button
            type="button"
            onClick={() => setOpen(false)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 8px',
              background: 'transparent', border: 'none',
              cursor: 'pointer', fontSize: '0.72rem',
              color: 'var(--text-tertiary)',
            }}
            title="Özet kartlarını gizle"
          >
            Analizi gizle ▲
          </button>
        </div>
      )}
      <div
      className="list-page-stats"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${compact ? 180 : 200}px, 1fr))`,
        gap: compact ? 10 : 12,
        marginBottom: 14,
      }}
    >
      {stats.map((s, idx) => {
        const colorCfg = COLOR_MAP[s.color || 'primary'] || DEFAULT_COLOR;
        const Component = s.onClick ? 'button' : 'div';
        return (
          <Component
            key={s.key || s.label || idx}
            onClick={s.onClick}
            style={{
              all: 'unset',
              display: 'block',
              background: 'var(--bg-card)',
              border: '1px solid var(--border-secondary)',
              borderRadius: 'var(--r-lg)',
              padding: compact ? '10px 12px' : '12px 14px',
              boxShadow: 'var(--shadow-xs)',
              cursor: s.onClick ? 'pointer' : 'default',
              transition: 'all 0.15s',
              ...(s.onClick ? { ':hover': { borderColor: colorCfg.fg } } : {}),
            }}
            onMouseEnter={s.onClick ? (e) => { e.currentTarget.style.borderColor = colorCfg.fg; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; } : undefined}
            onMouseLeave={s.onClick ? (e) => { e.currentTarget.style.borderColor = 'var(--border-secondary)'; e.currentTarget.style.boxShadow = 'var(--shadow-xs)'; } : undefined}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              {s.icon && (
                <div style={{
                  width: compact ? 30 : 36,
                  height: compact ? 30 : 36,
                  borderRadius: 'var(--r-md)',
                  background: colorCfg.bg,
                  color: colorCfg.fg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>{s.icon}</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  flexWrap: 'wrap',
                  rowGap: 2,
                }}>
                  <span style={{
                    fontSize: compact ? '1.15rem' : '1.35rem',
                    fontWeight: 800,
                    fontFamily: 'monospace',
                    color: colorCfg.fg,
                    lineHeight: 1.1,
                  }}>
                    {loading ? '…' : fmtNumber(s.value)}
                  </span>
                  <DeltaBadge delta={s.delta} tooltip={s.deltaTooltip} />
                </div>
                <div style={{
                  fontSize: '0.78rem',
                  color: 'var(--text-secondary)',
                  marginTop: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {s.label}
                </div>
                {s.sublabel && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginTop: 1 }}>
                    {s.sublabel}
                  </div>
                )}
              </div>
            </div>
          </Component>
        );
      })}
      </div>
    </div>
  );
}
